import React, { useRef, useMemo } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';

// Theme color mapping
const THEME_COLORS = {
  blue: '#4A90E2',
  pink: '#E84393',
  purple: '#7C4DFF',
  green: '#34C759',
  orange: '#FF9500'
};

// Stylized tech globe - no external textures needed
const TechGlobe = ({ themeColor = '#4A90E2' }) => {
  const globeRef = useRef();
  const wireframeRef = useRef();
  const ringsRef = useRef();
  const glowRef = useRef();

  useFrame(({ clock }) => {
    const t = clock.getElapsedTime();
    if (globeRef.current) {
      globeRef.current.rotation.y = t * 0.15;
    }
    if (wireframeRef.current) {
      wireframeRef.current.rotation.y = t * 0.12;
      wireframeRef.current.rotation.x = Math.sin(t * 0.1) * 0.1;
    }
    if (ringsRef.current) {
      ringsRef.current.rotation.z = t * 0.2;
    }
    if (glowRef.current) {
      glowRef.current.material.opacity = 0.15 + Math.sin(t) * 0.05;
    }
  });

  // Create gradient material for the globe
  const globeMaterial = useMemo(() => {
    return new THREE.MeshStandardMaterial({
      color: '#0a1929',
      metalness: 0.3,
      roughness: 0.7,
      transparent: true,
      opacity: 0.9,
    });
  }, []);

  return (
    <group>
      {/* Core globe */}
      <mesh ref={globeRef}>
        <sphereGeometry args={[1.8, 64, 64]} />
        <primitive object={globeMaterial} attach="material" />
      </mesh>

      {/* Wireframe overlay */}
      <mesh ref={wireframeRef}>
        <sphereGeometry args={[1.85, 32, 32]} />
        <meshBasicMaterial
          color={themeColor}
          wireframe
          transparent
          opacity={0.3}
        />
      </mesh>

      {/* Latitude/longitude lines */}
      <mesh rotation={[Math.PI / 2, 0, 0]}>
        <torusGeometry args={[1.86, 0.008, 16, 100]} />
        <meshBasicMaterial color={themeColor} transparent opacity={0.5} />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, Math.PI / 6]}>
        <torusGeometry args={[1.6, 0.006, 16, 100]} />
        <meshBasicMaterial color={themeColor} transparent opacity={0.3} />
      </mesh>
      <mesh rotation={[Math.PI / 2, 0, -Math.PI / 6]}>
        <torusGeometry args={[1.6, 0.006, 16, 100]} />
        <meshBasicMaterial color={themeColor} transparent opacity={0.3} />
      </mesh>

      {/* Vertical meridian */}
      <mesh>
        <torusGeometry args={[1.86, 0.008, 16, 100]} />
        <meshBasicMaterial color={themeColor} transparent opacity={0.5} />
      </mesh>

      {/* Orbiting ring */}
      <group ref={ringsRef}>
        <mesh rotation={[Math.PI / 3, 0.2, 0]}>
          <torusGeometry args={[2.1, 0.012, 16, 100]} />
          <meshBasicMaterial color={themeColor} transparent opacity={0.6} />
        </mesh>
        {/* Ring dots */}
        {[...Array(8)].map((_, i) => {
          const angle = (i / 8) * Math.PI * 2;
          const radius = 2.1;
          return (
            <mesh
              key={i}
              position={[
                Math.cos(angle) * radius * Math.cos(Math.PI / 3),
                Math.sin(angle) * radius,
                Math.cos(angle) * radius * Math.sin(Math.PI / 3) * 0.2
              ]}
              rotation={[Math.PI / 3, 0.2, 0]}
            >
              <sphereGeometry args={[0.035, 16, 16]} />
              <meshBasicMaterial color={themeColor} />
            </mesh>
          );
        })}
      </group>

      {/* Inner glow */}
      <mesh ref={glowRef}>
        <sphereGeometry args={[2.0, 64, 64]} />
        <meshBasicMaterial
          color={themeColor}
          transparent
          opacity={0.15}
          side={THREE.BackSide}
        />
      </mesh>

      {/* Outer glow */}
      <mesh>
        <sphereGeometry args={[2.2, 64, 64]} />
        <meshBasicMaterial
          color={themeColor}
          transparent
          opacity={0.08}
          side={THREE.BackSide}
        />
      </mesh>

      {/* Data points on globe surface */}
      {[...Array(20)].map((_, i) => {
        const phi = Math.acos(-1 + (2 * i) / 20);
        const theta = Math.sqrt(20 * Math.PI) * phi;
        return (
          <mesh
            key={i}
            position={[
              1.87 * Math.cos(theta) * Math.sin(phi),
              1.87 * Math.sin(theta) * Math.sin(phi),
              1.87 * Math.cos(phi)
            ]}
          >
            <sphereGeometry args={[0.025, 8, 8]} />
            <meshBasicMaterial color={themeColor} />
          </mesh>
        );
      })}
    </group>
  );
};

const Globe3D = ({ size = 280, theme = 'blue' }) => {
  const themeColor = THEME_COLORS[theme] || THEME_COLORS.blue;

  return (
    <div style={{ width: size, height: size, overflow: 'visible' }}>
      <Canvas
        camera={{ position: [0, 0, 6], fov: 45 }}
        style={{ background: 'transparent', overflow: 'visible' }}
        gl={{ alpha: true, antialias: true }}
      >
        <ambientLight intensity={0.3} />
        <directionalLight position={[5, 3, 5]} intensity={0.8} />
        <pointLight position={[-5, -3, -5]} intensity={0.4} color={themeColor} />
        <pointLight position={[0, 5, 0]} intensity={0.2} color="#ffffff" />
        <TechGlobe themeColor={themeColor} />
      </Canvas>
    </div>
  );
};

export default Globe3D;
